import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { pdfFontFamily, pdfPageFrame } from '../theme';
import type { DocumentMeta } from '../types';
import { fmtDate } from '../format';

interface Props {
  meta: DocumentMeta;
}

/** What a document needs no ink to be valid. */
const VALID_NOTICE = 'This document is computer generated and does not require signature or stamp to be considered valid.';
/** What a document with places to sign says instead. */
const SIGNED_NOTICE = 'This document is computer generated and is valid only when signed where indicated.';

const styles = StyleSheet.create({
  // Explicit height: an absolutely positioned fixed View anchored by
  // `bottom` is dropped by react-pdf when its height is content-derived.
  wrap: {
    position: 'absolute',
    bottom: pdfPageFrame.footerBottom,
    left: 0,
    right: 0,
    height: pdfPageFrame.footerHeight,
    paddingTop: 5,
    borderTopWidth: 0.6,
    borderTopColor: '#e9ecef',
    borderTopStyle: 'solid',
  },
  // Explicit width on each line: react-pdf measures `render` text after
  // layout, so a content-sized line collapses. No lineHeight here or
  // inherited from the page — react-pdf silently drops `render` text that
  // carries one.
  line: {
    width: '100%',
    textAlign: 'center',
    fontFamily: pdfFontFamily.serif,
    fontSize: 7.5,
    color: '#666666',
  },
});

/**
 * Every page's footer: two centred lines under a page-wide hairline — what
 * makes the document valid, then "Generated on 03 Nov 2026 • Page 1 of 2".
 * Pages are counted within their own <Page>, so each transcript in a bulk
 * print starts at Page 1.
 */
const Footer: React.FC<Props> = ({ meta }) => (
  <View style={styles.wrap} fixed>
    {/* The lines live one level inside the fixed View: react-pdf drops a
        `render` Text that is a direct child of an absolutely positioned one. */}
    <View>
      <Text style={styles.line}>{meta.footerNote ?? (meta.signed ? SIGNED_NOTICE : VALID_NOTICE)}</Text>
      <Text
        style={[styles.line, { marginTop: 2.5 }]}
        render={({ subPageNumber, subPageTotalPages }) => `Generated on ${fmtDate(meta.generatedAt)} • Page ${subPageNumber} of ${subPageTotalPages}`}
      />
    </View>
  </View>
);

export default Footer;
